import type { Alpine } from 'alpinejs';

import { setHubspotEmail } from '@utils/demoEmailHandoff';

// Alpine entrypoint (wired via the @astrojs/alpinejs `entrypoint` option).
// Registers shared component behaviors so markup binds to named methods
// instead of inline expression soup.

export default (Alpine: Alpine) => {
	// Single-open main navigation: one dropdown at a time, production-style
	// instant hover on desktop (no timers — the trigger rows and flyout
	// hover areas are contiguous), click-driven on mobile. Methods read
	// this.openMenu through Alpine's reactive proxy, so every binding
	// tracks it correctly.
	Alpine.data('mainNav', () => ({
		showMenu: false,
		openMenu: null as number | null,
		isOpen(i: number) {
			return this.openMenu === i;
		},
		enter(i: number) {
			if (window.innerWidth >= 1024) this.openMenu = i;
		},
		leave(i: number) {
			if (window.innerWidth >= 1024 && this.openMenu === i)
				this.openMenu = null;
		},
		toggle(i: number) {
			this.openMenu = this.openMenu === i ? null : i;
		},
		close(i: number) {
			if (this.openMenu === i) this.openMenu = null;
		},
	}));

	// Demo-request modal (components/reskin/DemoFormModal.astro).
	// `formId` is the embedded HubSpot demo form; `open(email)` drops the
	// address the hero captured into it before the panel animates in, then
	// moves focus past the prefilled email to the first empty field once the
	// enter transition (300ms) has settled and HubSpot has revealed its
	// dependent name fields. x-trap handles the trap, scroll lock, inert
	// siblings, and focus restore on close.
	Alpine.data('demoFormModal', (formId: string) => ({
		isOpen: false,
		open(email?: string) {
			if (email) setHubspotEmail(formId, email, { force: true });
			this.isOpen = true;
			window.setTimeout(() => this.focusFirstEmptyField(), 320);
		},
		close() {
			this.isOpen = false;
		},
		focusFirstEmptyField() {
			if (!this.isOpen) return;
			const root = this.$root as HTMLElement;
			const fields = root.querySelectorAll<
				HTMLInputElement | HTMLSelectElement
			>(
				'form input:not([type="hidden"]):not([type="checkbox"]):not([type="submit"]), form select'
			);
			const target = Array.from(fields).find(
				(el) => !el.value && el.offsetParent !== null
			);
			target?.focus({ preventScroll: true });
		},
	}));
};
